import React from 'react'
import { useTranslation } from 'react-i18next'

import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import LoadingButton from '@mui/lab/LoadingButton'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

import { Log } from '../../types/log'
import { PagedResponse } from '../../types/pagedResponse'
import { get } from '../../utils/service'
import { getEventsFromLogOrDatapoints, getLogName, getLogsUrl } from '../../utils/logUtils'
import { Profile } from '../../types/profile'
import { BasicDataParam } from '../../types/enums/graphDataParam'
import {
  convertProfileToDisplayUnits,
  getMainProfileParam,
  getProfileSeries,
  isBtBasedProfile,
} from '../../utils/profileEditorUtils'
import { ProfileEditorSeries, ProfileEditorSeriesParam } from '../../types/profileEditorSeries'
import { SeriesType } from '../../types/enums/seriesType'
import { LegacyDatapoint } from '../../types/datapoint'
import { ProfileType, ProfileTypeString } from '../../types/enums/profileType'
import { convertToTempScale, datapointsToSeries, getLogEvents } from '../../utils/graphUtils'
import { GraphSeriesEvents } from '../../types/graphSeriesConfig'
import { toReadableTimestamp } from '../../utils/dateUtils'
import Pagination from '../fields/pagination'
import Alert from '../alert/alert'
import SearchField from '../logs/logSearchField'

enum CompareType {
  Profile = 'profile',
  Log = 'log',
}

interface CompareSeriesDialogProps {
  profileBase: Profile
  customerId: number
  currentMachineId: number
  onClose: () => void
  onSelectPoints: (comparePoints: Array<ProfileEditorSeries>, logEvents: GraphSeriesEvents | null) => void
}

const CompareSeriesDialog = (props: CompareSeriesDialogProps): JSX.Element => {
  const { t } = useTranslation()

  const [selectedCompareType, setSelectedCompareType] = React.useState<CompareType>(CompareType.Profile)

  const [compareLogs, setCompareLogs] = React.useState<Array<Log> | null>(null)
  const [logSearchString, setLogSearchString] = React.useState<string>('')
  const [isLoadingCompareLogs, setIsLoadingCompareLogs] = React.useState<boolean>(false)
  const [hasCompareLogsError, setHasCompareLogsError] = React.useState<boolean>(false)
  const [loadCompareLogsUrl, setLoadCompareLogsUrl] = React.useState<string>('')
  const [logsPaginationSettings, setLogsPaginationSettings] = React.useState<PagedResponse<Log> | null>(null)
  const [loadingDatapointsLogUrl, setLoadingDatapointsLogUrl] = React.useState<string>('')

  const [loadProfileHistoryIds, setLoadProfileHistoryIds] = React.useState<Array<number>>([])
  const [profileHistoryIdErrors, setProfileHistoryIdErrors] = React.useState<Array<number>>([])
  const [profileHistories, setProfileHistories] = React.useState<{ [profileId: string]: Array<Profile> }>({})

  const [compareProfiles, setCompareProfiles] = React.useState<Array<Profile> | null>(null)
  const [profileSearchString, setProfileSearchString] = React.useState<string>('')
  const [isLoadingCompareProfiles, setIsLoadingCompareProfiles] = React.useState<boolean>(false)
  const [hasCompareProfilesError, setHasCompareProfilesError] = React.useState<boolean>(false)
  const [loadCompareProfilesUrl, setLoadCompareProfilesUrl] = React.useState<string>('')
  const [profilesPaginationSettings, setProfilesPaginationSettings] = React.useState<PagedResponse<Profile> | null>(
    null
  )

  React.useEffect(() => {
    if (loadCompareLogsUrl) {
      setCompareLogs(null)
      setIsLoadingCompareLogs(true)
      setHasCompareLogsError(false)
      get<PagedResponse<Log>>(loadCompareLogsUrl)
        .then((response) => {
          setCompareLogs(response.data.results)
          setLogsPaginationSettings(response.data)
          setIsLoadingCompareLogs(false)
        })
        .catch(() => {
          setIsLoadingCompareLogs(false)
          setHasCompareLogsError(true)
        })
    }
  }, [loadCompareLogsUrl])

  React.useEffect(() => {
    if (loadCompareProfilesUrl) {
      setCompareProfiles(null)
      setIsLoadingCompareProfiles(true)
      setHasCompareProfilesError(false)
      get<PagedResponse<Profile>>(loadCompareProfilesUrl)
        .then((response) => {
          setCompareProfiles(response.data.results)
          setProfilesPaginationSettings(response.data)
          setIsLoadingCompareProfiles(false)
        })
        .catch(() => {
          setIsLoadingCompareProfiles(false)
          setHasCompareProfilesError(true)
        })
    }
  }, [loadCompareProfilesUrl])

  React.useEffect(() => {
    if (selectedCompareType === CompareType.Log) {
      setLoadCompareLogsUrl(getLogsUrl(props.currentMachineId, logSearchString))
    } else {
      const searchFragment = profileSearchString ? `&search=${profileSearchString}` : ''
      setLoadCompareProfilesUrl(
        `profiles/?customer=${props.customerId}&profile_type=${props.profileBase.profile_type}&is_bbp_profile=false${searchFragment}`
      )
    }
  }, [
    selectedCompareType,
    props.currentMachineId,
    logSearchString,
    profileSearchString,
    props.profileBase.profile_type,
    props.customerId,
  ])

  const loadProfileHistory = (profileId: number): void => {
    setLoadProfileHistoryIds((oldIds) => [...oldIds, profileId])
    setProfileHistoryIdErrors((oldErrors) => oldErrors.filter((x) => x !== profileId))
    get<Array<Profile>>(`profiles/${profileId}/parents/`)
      .then((result) => {
        setLoadProfileHistoryIds((oldIds) => oldIds.filter((x) => x !== profileId))
        setProfileHistories((oldHistory) => {
          return {
            ...oldHistory,
            [profileId]: result.data,
          }
        })
      })
      .catch(() => {
        setLoadProfileHistoryIds((oldIds) => oldIds.filter((x) => x !== profileId))
        setProfileHistoryIdErrors((oldErrors) => [...oldErrors, profileId])
      })
  }

  const onSelectLog = (log: Log): void => {
    setLoadingDatapointsLogUrl(log.url)
    get<Array<LegacyDatapoint>>(`datapoints/?page_size=all&log=${log.id}`).then((response) => {
      const mainProfileParam = getMainProfileParam(props.profileBase.profile_type)
      const isTempSeries = props.profileBase.profile_type !== ProfileType.POWER
      const series = [
        {
          data: datapointsToSeries(response.data || [], mainProfileParam).map((x) => {
            return {
              ...x,
              dataValue: isTempSeries && x.dataValue !== null ? convertToTempScale(x.dataValue, 2) : x.dataValue,
            }
          }),
          param: mainProfileParam,
          seriesType: isTempSeries ? SeriesType.TEMPERATURE : SeriesType.PERCENT,
        },
        {
          data: datapointsToSeries(response.data || [], BasicDataParam.FAN),
          param: BasicDataParam.FAN as ProfileEditorSeriesParam,
          seriesType: SeriesType.PERCENT,
        },
        {
          data: datapointsToSeries(response.data || [], BasicDataParam.RPM),
          param: BasicDataParam.RPM as ProfileEditorSeriesParam,
          seriesType: SeriesType.RPM,
        },
      ]
      // show BT series if editing a temperature profile
      if (props.profileBase.profile_type !== ProfileType.POWER && props.profileBase.profile_type !== ProfileType.BT) {
        series.push({
          data: datapointsToSeries(response.data || [], BasicDataParam.BT).map((x) => {
            return {
              ...x,
              dataValue: x.dataValue !== null ? convertToTempScale(x.dataValue, 2) : x.dataValue,
            }
          }),
          param: BasicDataParam.BT,
          seriesType: SeriesType.TEMPERATURE,
        })
      }
      const events = getEventsFromLogOrDatapoints(log, response.data)
      const logEvents = getLogEvents(
        events,
        log,
        t('editprofileview.batch'),
        datapointsToSeries(response.data || [], BasicDataParam.BT)
      )
      props.onSelectPoints(series, logEvents)
    })
  }

  const onSelectProfile = (profile: Profile): void => {
    props.onSelectPoints(getProfileSeries(convertProfileToDisplayUnits(profile)), null)
  }

  return (
    <Dialog open={true} fullWidth sx={{ height: '100%' }} onClose={() => props.onClose()}>
      <DialogContent>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('editprofileview.compareprofilewith')}</FormLabel>
          <RadioGroup
            aria-label={t('editprofileview.compareprofilewith')}
            name="adjust-values-buttons-group"
            value={selectedCompareType}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              setSelectedCompareType(event.target.value as CompareType)
            }}
          >
            <FormControlLabel
              value={CompareType.Profile}
              control={<Radio />}
              label={`${t('editprofileview.other')} ${t(ProfileTypeString[props.profileBase.profile_type])}`}
            />
            {selectedCompareType === CompareType.Profile && (
              <Stack spacing={1} sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}>
                <SearchField
                  placeholder={t('editprofileview.searchprofiles')}
                  initialSearchString={profileSearchString}
                  onSearch={(searchString: string): void => {
                    if (searchString !== profileSearchString) {
                      setProfileSearchString(searchString)
                      setProfilesPaginationSettings(null)
                    }
                  }}
                />
                {isLoadingCompareProfiles && <CircularProgress />}
                <Stack sx={{ overflow: 'auto' }} spacing={1}>
                  {(compareProfiles || []).map((profile) => {
                    return (
                      <React.Fragment key={profile.url}>
                        <Stack direction="row">
                          {profile.parent ? (
                            <LoadingButton
                              sx={{ minWidth: '40px' }}
                              loading={loadProfileHistoryIds.indexOf(profile.id) > -1}
                              onClick={() => {
                                if (!!profileHistories[profile.id]) {
                                  setProfileHistories((oldProfileHistories) => {
                                    const changedHistory = { ...oldProfileHistories }
                                    delete changedHistory[profile.id]
                                    return changedHistory
                                  })
                                } else {
                                  loadProfileHistory(profile.id)
                                }
                              }}
                            >
                              {loadProfileHistoryIds.indexOf(profile.id) === -1 && !profileHistories[profile.id] && (
                                <Tooltip title={t('editprofileview.showprofilehistory')}>
                                  <AddIcon />
                                </Tooltip>
                              )}
                              {loadProfileHistoryIds.indexOf(profile.id) === -1 && !!profileHistories[profile.id] && (
                                <Tooltip title={t('editprofileview.hideprofilehistory')}>
                                  <RemoveIcon />
                                </Tooltip>
                              )}
                            </LoadingButton>
                          ) : (
                            <Stack sx={{ width: '40px' }} />
                          )}
                          <Button
                            variant="outlined"
                            sx={{ textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start' }}
                            onClick={() => onSelectProfile(profile)}
                          >
                            {props.profileBase.url === profile.url
                              ? `${profile.name} (${t('editprofileview.thisprofile')})`
                              : profile.name}
                          </Button>
                        </Stack>
                        {!!profileHistories[profile.id] && (
                          <Stack sx={{ pl: 8 }} spacing={0.5}>
                            {profileHistories[profile.id].map((historicProfile) => {
                              return (
                                <Button
                                  key={historicProfile.id}
                                  variant="outlined"
                                  sx={{ textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start' }}
                                  onClick={() => onSelectProfile(historicProfile)}
                                >
                                  {props.profileBase.url === historicProfile.url
                                    ? `${historicProfile.name} (${t('editprofileview.thisprofile')})`
                                    : `${historicProfile.name} - ${toReadableTimestamp(historicProfile.modified)}`}
                                </Button>
                              )
                            })}
                          </Stack>
                        )}
                        {profileHistoryIdErrors.indexOf(profile.id) > -1 && (
                          <Alert severity="error">{t('editprofileview.profilehistoryerror')}</Alert>
                        )}
                      </React.Fragment>
                    )
                  })}
                  {hasCompareProfilesError && <Alert severity="error">{t('editprofileview.profilesloaderror')}</Alert>}
                  {profilesPaginationSettings && (
                    <Pagination
                      isLoading={isLoadingCompareProfiles}
                      paginationSettings={profilesPaginationSettings}
                      onChangePage={(newPageUrl: string) => setLoadCompareProfilesUrl(newPageUrl)}
                    />
                  )}
                </Stack>
              </Stack>
            )}
            {!isBtBasedProfile(props.profileBase.profile_type) && (
              <FormControlLabel value={CompareType.Log} control={<Radio />} label={t('editprofileview.batch')} />
            )}
            {selectedCompareType === CompareType.Log && (
              <Stack spacing={1} sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}>
                <SearchField
                  placeholder={t('editprofileview.searchlogs')}
                  initialSearchString={logSearchString}
                  onSearch={(searchString: string): void => {
                    if (searchString !== logSearchString) {
                      setLogSearchString(searchString)
                      setLogsPaginationSettings(null)
                    }
                  }}
                />
                {isLoadingCompareLogs && <CircularProgress />}
                <Stack sx={{ overflow: 'auto' }} spacing={1}>
                  {compareLogs &&
                    compareLogs.map((log) => {
                      return (
                        <LoadingButton
                          key={log.url}
                          disabled={!!loadingDatapointsLogUrl}
                          loading={loadingDatapointsLogUrl === log.url}
                          sx={{ textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start' }}
                          variant="outlined"
                          onClick={() => onSelectLog(log)}
                        >
                          <Stack sx={{ alignItems: 'flex-start' }}>
                            <Typography variant="body2">{getLogName(log, '', t('editprofileview.batch'))}</Typography>
                            <Typography variant="body2">
                              {log.profile_name || log.profile_data?.name || t('editprofileview.unknownprofile')}
                            </Typography>
                            <Typography variant="body2">{toReadableTimestamp(log.start_timestamp)}</Typography>
                          </Stack>
                        </LoadingButton>
                      )
                    })}
                  {hasCompareLogsError && <Alert severity="error">{t('editprofileview.logsloaderror')}</Alert>}
                  {logsPaginationSettings && (
                    <Pagination
                      isLoading={isLoadingCompareLogs}
                      paginationSettings={logsPaginationSettings}
                      onChangePage={(newPageUrl: string) => setLoadCompareLogsUrl(newPageUrl)}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onClose()}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompareSeriesDialog
